/**
 * 招待コード（2026-09-29 creator/launch-money・founder「最初の約 100 人に 5 ドル分」・MASTER-PLAN §0）の「React に依存しない部分」。
 * node --test で確かめる（inviteCode.test.ts）。画面は pages/AuthPage.tsx（登録）・pages/OAuthCallback.tsx（Google / GitHub の戻り）・
 * components/InviteCodeCard.tsx（登録の後に入れる・残高の画面）。
 *
 * サーバーの正（Core の口・docs/teams/creator/tasks/first-launch-money-api.md）:
 *   - POST /v1/developers/signup { …, inviteCode? } → 201 に invite: { credited, currency, pending: true }。無効ならアカウントを作らずに 400 / 409
 *   - POST /v1/developers/oauth/exchange { code, verifier, inviteCode? } → 新規なら invite: { credited, currency, pending: false }・
 *     既存のアカウントなら invite: { ignored: true, reason: 'existing_account' }（ログインは通す）
 *   - POST /v1/developers/me/invite-code { code } → 200 { credited, currency, pending }（kds_ だけ）
 *   - 招待なしの本体（signupRequiresInvite: false・2026-10-29）: 上の 2 つの登録と作り手を始める口は、使えないコードでも 201 / 200 で作り、
 *     invite の代わりに inviteNotApplied: { reason }（下の inviteNotAppliedReason）
 *   - GET /v1/money/prepaid の invite: { granted, remaining, pending } | null（Money）
 *   - エラー: 400 invalid_invite_code・409 invite_code_used・409 invite_limit_reached・409 already_redeemed
 *
 * WHY 形を画面でも見るか: サーバーも形を先に見る（Core の parseInviteCode）が、OAuth は「プロバイダの画面へ行って戻ってから」断られると
 *   往復が丸ごと無駄になる。形の違い（打ち間違い）だけは押した時点で止める。正はサーバー（使用済み・上限はサーバーだけが分かる）。
 * WHY 招待コードを sessionStorage の控え（devOAuth.ts の PendingOAuth）に入れるか: Core が start ではなく exchange で受ける設計
 *   （開始した側の verifier が要る = 他人が差し込めない）。プロバイダの画面へ移る間に画面のメモリは消えるので、verifier と同じ控えに持つ。
 *   控えは一回限り・10 分・このタブだけ（devOAuth.ts の WHY）= コードもそれ以上残らない。localStorage には置かない。
 *
 * このファイルは import を持たない（featureToggle.ts と同じ理由: `node --test` で型の除去だけで動かす）。
 */

/**
 * コードに使う文字と長さ（サーバーの apps/server/src/modules/money/inviteCredit.ts の INVITE_CODE_ALPHABET・INVITE_CODE_LENGTH と同じ）。
 * 紛らわしい文字（0 / O / 1 / I / L）を除いた英大文字と数字。テストがサーバーの値と突き合わせる（片方だけ変えると、正しいコードを画面が断る）
 */
export const INVITE_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const INVITE_CODE_LENGTH = 16;
const CODE_RE = new RegExp(`^[${INVITE_CODE_ALPHABET}]{${INVITE_CODE_LENGTH}}$`);
/**
 * 人数と期限のある招待コード（動画の概要欄に貼る・例 CHATGPT-01-7KQM・2026-10-12 money/campaign-invite・サーバーの 0313）の形:
 * ハイフンと空白を除いて英大文字と数字 6〜32 文字（サーバーの CAMPAIGN_CODE_MIN / MAX と同じ・テストが突き合わせる）。
 * 紛らわしい文字（O と 0 など）の畳み込みはサーバーだけがする（ここは形を見るだけ = 畳まずに送っても同じコードになる）
 */
export const CAMPAIGN_CODE_MIN = 6;
export const CAMPAIGN_CODE_MAX = 32;
const CAMPAIGN_RE = new RegExp(`^[A-Z0-9]{${CAMPAIGN_CODE_MIN},${CAMPAIGN_CODE_MAX}}$`);

/**
 * 欄に入れた値を読む（サーバーの normalizeInviteCode と同じ: 空白とハイフンを除いて大文字に）。
 *   none    … 空（任意の欄なので「付けない」）
 *   ok      … 送る値（正規化した 16 文字・または人数と期限のあるコードの英数字 6〜32 文字。サーバーがもう一度正規化しても同じ）
 *   invalid … 形が違う = 送らずに invalid_invite_code の文言を出す
 * WHY 小文字・ハイフン・空白を許すか: 人がメールやメモから写すときに XXXX-XXXX-… の区切りや小文字で打つ。サーバーも許す
 */
export type InviteInput = { kind: 'none' } | { kind: 'ok'; code: string } | { kind: 'invalid' };

export function readInviteInput(raw: string | null | undefined): InviteInput {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { kind: 'none' };
  if (trimmed.length > 64) return { kind: 'invalid' };
  const code = trimmed.replace(/[\s-]/g, '').toUpperCase();
  // 1 回のコード（16 文字）か、人数と期限のあるコード（英数字 6〜32 文字）。後者の形は広い = 打ち間違いの多くはサーバーの 400 で分かる
  return CODE_RE.test(code) || CAMPAIGN_RE.test(code) ? { kind: 'ok', code } : { kind: 'invalid' };
}

/**
 * 招待コード入りの登録リンク（2026-10-16 console/invite-link・founder「紹介してくれる作り手に、コード入りのリンクを DM で渡す」）。
 *   https://dashboard.kumodeck.com/signup?invite=<CODE> → 登録の画面の招待コードの欄に <CODE> が入る。
 * 返す値: 欄に入れる文字（null = 何も入れない）。
 *   - 形が変（readInviteInput が ok でない・空・64 文字超）→ null。WHY: 変な文字を欄に出して「無効です」で迎えない = 空の欄と同じにする
 *   - 欄に入れるのは配った形のまま（前後の空白だけ除く）。WHY: CHATGPT-01-7KQM のようなハイフン入りを人が見て、DM の文と同じと分かるように。
 *     送るときは今までどおり readInviteInput が正規化する（サーバーも同じ）
 *   - invite が複数あるときは最初のものだけ（URLSearchParams.get と同じ）
 * ログイン済みの人は App.tsx の /signup → 元の画面への Navigate で、ここまで来ない（search も持っていかない = 何もしない）。
 */
export function inviteFromSearch(search: string): string | null {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get('invite');
  } catch {
    return null;
  }
  const trimmed = (raw ?? '').trim();
  return readInviteInput(trimmed).kind === 'ok' ? trimmed : null;
}

/**
 * 招待コードを読んだ後の URL の search（invite だけ除く・他の値はそのまま）。'' なら ? も付けない。
 * WHY 消すか: コードを URL に残すと、Google / GitHub の画面へ移るときの Referer・履歴・画面共有・アドレスバーの写しから漏れる。
 *   public/_headers の Referrer-Policy（strict-origin-when-cross-origin）でも他所へはオリジンだけだが、URL から消すのが確実（二重の守り）。
 *   コードは欄（画面のメモリ）と、Google / GitHub の場合はいつもの控え（sessionStorage・一回限り）にだけ持つ。
 */
export function searchWithoutInvite(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('invite');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * 招待コードのエラーのコード（サーバーの 4 つ + 招待制の invite_code_required）。文言は i18n の `error.<code>`（errorText が引く・en.invite.ts）。
 * invite_code_required（2026-10-06 core/invite-only）も入れる = Google / GitHub の新規登録がコード無しで断られたら、ログインの画面から押した人も
 * 登録の画面（コードの欄がある）へ戻す（pages/OAuthCallback.tsx の isInviteError）
 */
// invite_code_expired / invite_code_used_up（2026-10-12）: 人数と期限のあるコードだけが返す（期限切れ・取り消し / 人数に達した）
export const INVITE_ERRORS = ['invalid_invite_code', 'invite_code_used', 'invite_limit_reached', 'already_redeemed', 'invite_code_required', 'invite_code_expired', 'invite_code_used_up'] as const;
export type InviteError = (typeof INVITE_ERRORS)[number];

export const isInviteError = (code: unknown): code is InviteError => typeof code === 'string' && (INVITE_ERRORS as readonly string[]).includes(code);

/**
 * 広告から来た人の登録（2026-10-18 管理・docs/teams/site/tasks/early-access.md）。サイトの /early（apps/site/src/early.mjs）の
 * 「Get early access」は /signup?invite=<その広告のコード>&src=<ラベル> を開く。src は Ops が広告ごとに数えるラベル（計画 §3）。
 * 返す値: 形の合う src（英数字と . _ - の 40 文字まで・サイトの assets/early.js と同じ規則）か null。
 * src は URL に残る（searchWithoutInvite は invite だけ消す）= 読み直しても広告から来たと分かる。コードではないので残してよい
 */
export function adSourceFromSearch(search: string): string | null {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get('src');
  } catch {
    return null;
  }
  const src = (raw ?? '').trim();
  return /^[A-Za-z0-9._-]{1,40}$/.test(src) ? src : null;
}

/**
 * 「このリンクの枠は埋まりました」の文（i18n の invite.earlyFull・広告の計画 §2）を出すエラー。
 *   invite_code_used_up  … 広告のコードが人数（管理の invite:create --max-uses 20）に達した = 計画の言う「枠が埋まった」の本体
 *   invite_limit_reached … 招待クレジット全体が配り終わった（指示書が名指しした 409）。広告から来た人には同じく「枠が埋まった」
 * WHY 広告から来た人だけか: 同じエラーは DM のリンク・動画の概要欄のコードでも返る。「このリンク」「early access」の文はそちらに合わない
 *   = いつもの error.<code> のまま。期限切れ（invite_code_expired）は「埋まった」ではないので入れない
 */
export const EARLY_FULL_ERRORS = ['invite_code_used_up', 'invite_limit_reached'] as const satisfies readonly InviteError[];

/** 招待コードのエラーの文言のキー。広告から来た人の「枠が埋まった」だけ invite.earlyFull、ほかは今までどおり error.<code> */
export function inviteErrorKey(code: InviteError, fromAd: boolean): 'invite.earlyFull' | `error.${InviteError}` {
  return fromAd && (EARLY_FULL_ERRORS as readonly string[]).includes(code) ? 'invite.earlyFull' : `error.${code}`;
}

/**
 * OAuth の交換（POST /v1/developers/oauth/exchange）の本文。控えに招待コードがあり、ログイン（新規登録）の往復のときだけ付ける。
 * link / reauth（ログイン済みの人の連携・本人確認）には付けない: 新規登録ではない = サーバーも使わない。控えに入れるのも intent=login だけ
 */
export function exchangeBody(
  p: { verifier: string; intent: string; inviteCode?: string; src?: string },
  code: string
): { code: string; verifier: string; inviteCode?: string; src?: string } {
  const invite = p.intent === 'login' && typeof p.inviteCode === 'string' ? readInviteInput(p.inviteCode) : { kind: 'none' as const };
  // 登録の src（2026-10-19 ops/usage-origin §3）: ログインの往復で形の合うときだけ。一覧に当たるかはサーバーが決める（apps/server の signupSrc.ts = 一覧は 1 か所）。
  //   古いサーバーは知らない欄を読み捨てる（zod の object は余分なキーを落とす）
  const src = p.intent === 'login' && typeof p.src === 'string' ? adSourceFromSearch(`src=${encodeURIComponent(p.src)}`) : null;
  return { code, verifier: p.verifier, ...(invite.kind === 'ok' ? { inviteCode: invite.code } : {}), ...(src ? { src } : {}) };
}

/**
 * 交換・登録の応答の invite を読む。
 *   none     … 付けなかった / 古いサーバー（invite が無い）
 *   credited … 新規登録で入った（pending = メールの確認待ち。OAuth はプロバイダの確認済みのメールなので false）
 *   ignored  … 既存のアカウントへのログインだった（コードは使っていない）→「残高の画面から入れられます」と案内する
 */
export type InviteOutcome = { kind: 'none' } | { kind: 'credited'; credited: number; currency: string; pending: boolean } | { kind: 'ignored' };

export function inviteOutcome(invite: unknown): InviteOutcome {
  if (!invite || typeof invite !== 'object') return { kind: 'none' };
  const i = invite as { ignored?: unknown; credited?: unknown; currency?: unknown; pending?: unknown };
  if (i.ignored === true) return { kind: 'ignored' };
  if (typeof i.credited === 'number') return { kind: 'credited', credited: i.credited, currency: typeof i.currency === 'string' ? i.currency : 'usd', pending: i.pending === true };
  return { kind: 'none' };
}

/**
 * 招待なしの登録（2026-10-29 core/open-signup-build・サーバーの KUMO_SIGNUP_REQUIRES_INVITE=false・10/31 から本番）で、付けたコードが
 * 使えなかったがアカウントはできた（サーバーの inviteNotApplied: { reason }・apps/server/src/modules/platform/inviteCode.ts の InviteNotApplied）。
 * 3 つの口（メールの登録・Google / GitHub の exchange・作り手を始める）が同じ形で返す。
 *   InviteError … reason がここの一覧にある = 理由の文（error.<code>・広告の人は inviteErrorKey）を添えられる
 *   'unknown'   … reason が知らないコード（新しい本体）= 理由の文は出さず「使えませんでしたが、アカウントはできました」だけ
 *   null        … キーが無い（コードを付けていない・使えた・古い本体・招待制の本体 = 使えなければ 400 / 409 で断られる）
 * WHY invite とは別のキーか: サーバーの側の WHY と同じ（今の CLI が invite.credited を額として読む）
 */
export function inviteNotAppliedReason(res: unknown): InviteError | 'unknown' | null {
  if (!res || typeof res !== 'object' || !('inviteNotApplied' in res)) return null;
  const v = (res as { inviteNotApplied: unknown }).inviteNotApplied;
  if (!v || typeof v !== 'object') return null;
  const reason = (v as { reason?: unknown }).reason;
  return isInviteError(reason) ? reason : 'unknown';
}

/**
 * 「使えませんでしたが、アカウントはできました」の 1 枚（components/InviteNotAppliedNotice.tsx）の本文の選び方（2026-10-29 core/open-signup-polish）。
 *   lead = なぜクレジットが入らなかったか（理由ごとに 1 文）・tail = 次にできること（別のコード / 入金）。
 * WHY error.<code> をそのまま使わないか: あれは「登録する前・欄に入れる時」の文（「打ち間違いを確かめて」「新しいコードを探して」）。
 *   この 1 枚の時点ではアカウントはもうできている = 直して登録し直すことはない。前の形（箱 + error.<code> の小さな 1 行）は説明が二重だった（Rauno 役の確認）
 * 広告から来た人の「枠が埋まった」は inviteErrorKey と同じ見分け（EARLY_FULL_ERRORS）。already_redeemed / invite_code_required は
 *   新しいアカウントでは起きない（招待なしの本体はコード無しでも通す）が、来たら 'other'（言い過ぎない）
 */
export type NotAppliedLead = 'invalid' | 'ended' | 'used' | 'full' | 'earlyFull' | 'limit' | 'other';
export function notAppliedCopy(reason: InviteError | 'unknown', fromAd: boolean): { lead: NotAppliedLead; tail: 'code' | 'card' } {
  if (reason !== 'unknown' && fromAd && (EARLY_FULL_ERRORS as readonly string[]).includes(reason)) return { lead: 'earlyFull', tail: 'code' };
  switch (reason) {
    case 'invalid_invite_code':
      return { lead: 'invalid', tail: 'code' };
    case 'invite_code_expired':
      return { lead: 'ended', tail: 'code' };
    case 'invite_code_used':
      return { lead: 'used', tail: 'code' };
    case 'invite_code_used_up':
      return { lead: 'full', tail: 'code' };
    case 'invite_limit_reached':
      return { lead: 'limit', tail: 'card' };
    default:
      return { lead: 'other', tail: 'code' };
  }
}

/**
 * GET /v1/money/prepaid の invite（Money の prepaid.ts InviteStanding）。型の正はここ（moneyApi.ts の PrepaidView.invite がこれを使う・2026-09-29 launch-close-2 で寄せた）。
 *   granted   … 配った額（セント）
 *   remaining … 残り（前払い残高の内数。確認待ち・使い切りなら 0）
 *   pending   … メールの確認待ち（確認が済むと入る）
 */
export interface InviteStanding {
  granted: number;
  remaining: number;
  pending: boolean;
}

/** 応答の invite を読む（無い・null・壊れている = null = まだ招待コードを使っていない） */
export function readInviteStanding(v: unknown): InviteStanding | null {
  if (!v || typeof v !== 'object') return null;
  const i = v as Partial<Record<keyof InviteStanding, unknown>>;
  if (typeof i.granted !== 'number' || typeof i.remaining !== 'number') return null;
  return { granted: i.granted, remaining: i.remaining, pending: i.pending === true };
}

/**
 * 残高の画面の招待の欄が出すもの。
 *   form     … まだ使っていない = 入れる欄
 *   pending  … 押さえたがメールの確認待ち =「メールの確認が済むと入ります」
 *   credited … 入った =「うち招待クレジット {remaining}」
 */
export type InviteView = { kind: 'form' } | { kind: 'pending'; granted: number } | { kind: 'credited'; granted: number; remaining: number };

export function inviteView(s: InviteStanding | null): InviteView {
  if (!s) return { kind: 'form' };
  if (s.pending) return { kind: 'pending', granted: s.granted };
  return { kind: 'credited', granted: s.granted, remaining: s.remaining };
}

/**
 * OAuth の戻りで招待コードのせいで入れなかったとき、登録の画面（/signup）に渡す案内（pages/OAuthCallback.tsx → pages/AuthPage.tsx）。
 * WHY /login でなく /signup か: 招待コードの欄は登録の画面にある。無効・使用済み・上限は、コードを直すか外してやり直す話。
 *   サーバーは使用済み・上限のとき TX ごとロールバック = アカウントは作られていない（Core の flow.ts）。
 *   形の違いは一回限りのコードを消費する前に断る（Core）が、画面が押す前に止めるのでふつうは来ない
 */
export interface InviteSignupNotice {
  inviteError: InviteError;
  /** 広告から来た登録だった（控えの fromAd・上の adSourceFromSearch）。「枠が埋まった」の文を選ぶため（inviteErrorKey） */
  fromAd?: boolean;
}

/**
 * メールの確認が済んだ画面（pages/VerifyEmail.tsx）で「招待クレジット {amount} が入りました」を出すか（2026-09-30 console/core-first C5）。
 * 返すのは入った額（セント・GET /v1/money/prepaid の invite.granted）。分からなければ null = 今までどおり「確認しました」だけ。
 *
 * 2026-09-30 console/invite-fields から: **古い本体（確認の応答に invite のキーが無い）のときだけ** 使う。新しい本体は応答の invite を読む（下の verifyInviteDecision）。
 * WHY 確認の応答ではなく残高を読むか（古い本体）: 以前の POST /v1/developers/email/verify の応答は { ok, status } だけで、招待のことを返さない
 *   （サーバーは確認と同じ TX で入れる = apps/server/src/modules/platform/recovery.ts の creditInviteOnVerified）。
 *   なので画面は確認の後に残高の invite を読んでいた。
 * 「入った」と言える条件（どれか 1 つでも欠けたら出さない = 推測で「入りました」と言わない）:
 *   - wasUnverified: この画面を開いた時点でメールが未確認だった（セッションの emailVerified === false）。
 *     確認済みの人がリンクをもう一度押しただけなら、今回入ったわけではない
 *   - pending が false: 確認待ちが解けた（押さえていたクレジットが入った）
 *   - granted > 0 かつ remaining > 0: 額があり、まだ残っている（確認の前は使えないので、今入ったなら残っている）
 */
export function inviteCreditedByVerify(wasUnverified: boolean, standing: InviteStanding | null): number | null {
  if (!wasUnverified || !standing || standing.pending) return null;
  return standing.granted > 0 && standing.remaining > 0 ? standing.granted : null;
}

/**
 * GET /v1/platform/info の inviteCredit（2026-09-30 core/invite-fields・Console と合意。サーバーの正: apps/server/src/modules/platform/routes.ts →
 * Money の inviteCreditOffer）を登録の画面の招待コードの欄に使う形に読む（2026-09-30 console/invite-fields）。
 *   offer   … { amountCents, currency } = 欄を出し、説明に額を出す（額は運営が変えられる設定の行 = 画面に直書きしない）
 *   off     … null = 招待の仕組みが使えない本体（上限 0 など）→ **欄を出さない**（押しても必ず断られる欄を見せない）
 *   unknown … キーが無い（古い本体）・読み込み中・読めなかった・形が壊れている → 今までの欄と額なしの説明（invite.fieldHintCredit）
 * WHY 壊れた形を off でなく unknown にするか: off は「欄を消す」= 招待コードを持っている人が入れられなくなる。
 *   分からないときは今までどおり出しておき、正はサーバー（使えなければ登録の時に 400 / 409 で断る）に任せる。
 * WHY 「枠が埋まった」を見ないか: サーバーは残りの枠を返さない（埋まっても null にならない = Core と合意）。埋まった後は登録の時の 409 invite_limit_reached の文言で伝わる
 */
export type InviteCreditOffer = { kind: 'offer'; amountCents: number; currency: string } | { kind: 'off' } | { kind: 'unknown' };

/**
 * platform/info の応答全体から inviteCredit を読む（キーが「無い」と「null」を分けるため、応答全体を受ける）。
 * currency が無い・文字列でないときは 'usd'（サーバーは 'usd' だけを返す = 前払いの単位）
 */
export function readInviteCreditOffer(info: unknown): InviteCreditOffer {
  if (!info || typeof info !== 'object' || !('inviteCredit' in info)) return { kind: 'unknown' };
  const v = (info as { inviteCredit: unknown }).inviteCredit;
  if (v === null) return { kind: 'off' };
  if (typeof v !== 'object') return { kind: 'unknown' };
  const o = v as { amountCents?: unknown; currency?: unknown };
  if (typeof o.amountCents !== 'number' || !Number.isInteger(o.amountCents) || o.amountCents <= 0) return { kind: 'unknown' };
  return { kind: 'offer', amountCents: o.amountCents, currency: typeof o.currency === 'string' && o.currency ? o.currency : 'usd' };
}

/**
 * 登録の画面が送る招待コード。欄を出さない本体（off）では、欄に何が残っていても送らない
 * = メールの登録の本文にも、Google / GitHub の控え（sessionStorage → exchange）にも入れない（指示書 invite-fields 1）。
 * WHY 欄の値を消すだけでなくここでも止めるか: 読み込み中（unknown）に打った後で off が届くと、見えない欄の値が残る。見えない値を送らない
 */
export function inviteInputToSend(offer: InviteCreditOffer, input: InviteInput, requirement: SignupInviteRequirement = 'unknown'): InviteInput {
  // 招待制（required）では、クレジットが無い本体（off）でもコードは「入るための鍵」= 送る（2026-10-06 core/invite-only）
  if (requirement === 'required') return input;
  return offer.kind === 'off' ? { kind: 'none' } : input;
}

/**
 * 招待制（2026-10-06 founder「新規登録を招待制に」・core/invite-only）。サーバーの正: GET /v1/platform/info の signupRequiresInvite
 * （env KUMO_SIGNUP_REQUIRES_INVITE・未設定 = true）。true なら新しいアカウント（メール・Google・GitHub）に招待コードが要り、
 * コード無しは 400 invite_code_required（アカウントを作らない）。
 *   required … true = 欄を必須にし、押す前に止める
 *   optional … false = 今までどおり（任意の欄・クレジットのためだけ）
 *   unknown  … キーが無い（古い本体）・読み込み中・読めなかった = 欄は出し「任意」とは言わないが、押す前には止めない（正はサーバー）
 * WHY unknown で「任意」と書かないか: サーバーの既定は招待制。読み込みの一瞬「(optional)」が出てから消えると、空のまま押す人が出る
 */
export type SignupInviteRequirement = 'required' | 'optional' | 'unknown';

export function readSignupRequiresInvite(info: unknown): SignupInviteRequirement {
  if (!info || typeof info !== 'object' || !('signupRequiresInvite' in info)) return 'unknown';
  const v = (info as { signupRequiresInvite: unknown }).signupRequiresInvite;
  return v === true ? 'required' : v === false ? 'optional' : 'unknown';
}

/**
 * 登録の画面の招待コードの欄を出すか・「(optional)」を付けるか。
 * 招待制なら招待クレジットが無い本体（inviteCredit: null = off）でも出す（欄が無いと誰も登録できない）。
 * 任意の本体は今までどおり（off なら出さない = 押しても必ず断られる欄を見せない・console/invite-fields）
 */
export function inviteFieldState(requirement: SignupInviteRequirement, offer: InviteCreditOffer): { show: boolean; optional: boolean } {
  if (requirement === 'required') return { show: true, optional: false };
  if (requirement === 'optional') return { show: offer.kind !== 'off', optional: true };
  return { show: offer.kind !== 'off', optional: false };
}

/**
 * 押す前に止めるエラー（メールの登録・Google / GitHub のボタンで同じ判断）。null = 送ってよい。
 * 招待制で空 → invite_code_required（プロバイダの画面へ行って戻ってから断られると往復が丸ごと無駄 + 一回限りのコードを燃やす）。
 * 形の違い → invalid_invite_code（今までどおり）。使用済み・上限はサーバーだけが分かる
 */
export function signupInviteError(requirement: SignupInviteRequirement, input: InviteInput): 'invite_code_required' | 'invalid_invite_code' | null {
  if (input.kind === 'invalid') return 'invalid_invite_code';
  if (input.kind === 'none' && requirement === 'required') return 'invite_code_required';
  return null;
}

/**
 * POST /v1/developers/email/verify の応答の invite（2026-09-30 core/invite-fields・サーバーの正: platform/recovery.ts と inviteCode.ts の verifyInviteOf）を
 * メールの確認の完了の画面（pages/VerifyEmail.tsx）の判断にする（2026-09-30 console/invite-fields）。
 *   credited … invite.credited === true = この確認で入った →「招待クレジット {額} が入りました」（額は応答の値）
 *   none     … invite が null（招待コードを使っていない・期限切れで押さえが外れた）か credited: false → 招待の文は出さない
 *   legacy   … キー自体が無い → 古い本体。残高の読み直し（inviteCreditedByVerify）で判断する
 * WHY 応答を信じてよいか: サーバーは invite を「本人のセッションで今回確認できた応答（status: 'verified'）」にだけ付ける。
 *   確認済みのリンクをもう一度開いた応答・password_required には付かない = 画面で「開いた時点で未確認だったか」を重ねて見なくてよい。
 *   キーが無いときは古い本体か「もう一度開いた」なので、今までどおり wasUnverified と残高で慎重に判断する（推測で「入りました」と言わない）。
 * 形が壊れている（credited: true なのに額が無い等）は none: 額の分からない「入りました」は出さない
 */
export type VerifyInviteDecision = { kind: 'credited'; amount: number; currency: string } | { kind: 'none' } | { kind: 'legacy' };

export function verifyInviteDecision(res: unknown): VerifyInviteDecision {
  if (!res || typeof res !== 'object' || !('invite' in res)) return { kind: 'legacy' };
  const v = (res as { invite: unknown }).invite;
  if (!v || typeof v !== 'object') return { kind: 'none' };
  const i = v as { credited?: unknown; amountCents?: unknown; currency?: unknown };
  if (i.credited !== true || typeof i.amountCents !== 'number' || !(i.amountCents > 0)) return { kind: 'none' };
  return { kind: 'credited', amount: i.amountCents, currency: typeof i.currency === 'string' && i.currency ? i.currency : 'usd' };
}
